import type { SupabaseClient } from "@supabase/supabase-js";
import { ensureProfile } from "../profile/ensure-profile";
import type { Capture } from "./schema";
import { uploadCapture } from "./upload";
import {
  isNetworkMotif,
  missingSessionFailure,
  RESEAU_PERDU,
  SESSION_EXPIREE,
  uploadErrorMessage,
} from "./upload-error";

// `audioMissing` : l'échec vient d'un fichier audio local définitivement
// introuvable (≠ erreur transitoire réseau/auth). Propagé depuis `uploadCapture`
// → permet au filet de re-synchro d'ABANDONNER la capture au lieu de boucler.
// `detail` : motif technique brut (journal, classement, signalement) — jamais
// affiché ; `error` reste la phrase à nous.
export type SyncCaptureResult =
  | { ok: true }
  | { ok: false; error: string; audioMissing?: boolean; detail?: string };

export interface SyncCaptureInput {
  client: SupabaseClient;
  capture: Capture;
  user: { id: string; email: string | null | undefined };
  supabaseUrl: string;
  anonKey: string;
  // Injection pour les tests.
  ensureProfileFn?: typeof ensureProfile;
  uploadCaptureFn?: typeof uploadCapture;
}

// Cœur réseau de la synchro (profil + upload audio + insert capture),
// réutilisable hors CaptureScreen (re-synchro auto des captures en attente au
// lancement). Zéro état React. ROBUSTE réseau : toute exception (ex. WKWebView
// « TypeError: Load failed » sur getSession après un changement de réseau) est
// convertie en {ok:false} — jamais un throw qui remonterait en écran d'erreur brut.
export async function syncCaptureToServer(input: SyncCaptureInput): Promise<SyncCaptureResult> {
  const ensureProfileFn = input.ensureProfileFn ?? ensureProfile;
  const uploadCaptureFn = input.uploadCaptureFn ?? uploadCapture;
  try {
    // Session lue AVANT toute écriture (C20, tour 2) : sans elle, le profil
    // part sous la clé anonyme et la RLS le refuse (HTTP 401) — un refus qui
    // passerait pour un « problème » alors qu'il suffit de se reconnecter.
    const {
      data: { session },
      error: sessionError,
    } = await input.client.auth.getSession();
    // Session nulle sur un rafraîchissement impossible (réseau, 502/503/504)
    // ⇒ réseau, pas session (C20, tour 4).
    if (!session) return missingSessionFailure("captures.sync", sessionError);
    const profile = await ensureProfileFn(input.client, {
      id: input.user.id,
      email: input.user.email,
    });
    if (!profile.ok) return profile;
    return await uploadCaptureFn({
      client: input.client,
      capture: input.capture,
      supabaseUrl: input.supabaseUrl,
      anonKey: input.anonKey,
    });
  } catch (e) {
    // DS §6.13 : une exception non prévue (réseau, plugin natif) ne devient pas
    // un message à l'écran.
    const detail = e instanceof Error ? e.message : String(e);
    return { ok: false, error: uploadErrorMessage("captures.sync", detail), detail };
  }
}

// Affine un message DÉJÀ RÉDIGÉ par la chaîne d'envoi. Le DS §6.13 reprochait
// à cette fonction de retomber sur le texte brut quand le motif n'était pas
// reconnu : ce n'est plus un risque, parce que ses trois sources
// (`resumable-upload`, `upload`, le catch de `syncCaptureToServer`) passent
// maintenant toutes par `uploadErrorMessage`, qui journalise et rend une de nos
// phrases. Le repli rend donc une phrase à nous, jamais un motif technique —
// l'étanchéité est en amont, ici il ne reste qu'un raffinement.
export function friendlyNetworkError(error: string): string {
  if (/load failed|failed to fetch|network ?error/i.test(error)) {
    return RESEAU_PERDU;
  }
  return error;
}

export type SyncFailureCause = "network" | "session" | "other";

// Jeton refusé ou expiré en route : HTTP 401 (base, RLS sous la clé anonyme ;
// TUS) ou « JWT expired ». Le geste est de se reconnecter.
const SESSION_MOTIF = /(?:\bHTTP |response code: )401\b|jwt expired/i;

/**
 * Classe d'un échec de synchro d'une capture de prise (C20, R70) :
 * - `network` : hors ligne, connexion perdue, base ou stockage injoignables —
 *   réessayer plus tard réussira (« En préparation ») ;
 * - `session` : session expirée — le seul geste est de se reconnecter ;
 * - `other` : refus de la base, profil absent, audio illisible… — réessayer ne
 *   suffira pas (« Problème signalé », signalement automatique).
 */
export function classifySyncFailure(
  r: { error: string; detail?: string },
  online: boolean,
): SyncFailureCause {
  if (!online) return "network";
  if (r.error === SESSION_EXPIREE || SESSION_MOTIF.test(r.detail ?? "")) return "session";
  if (r.error === RESEAU_PERDU || isNetworkMotif(r.error) || isNetworkMotif(r.detail ?? "")) {
    return "network";
  }
  return "other";
}
